import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import Cookies from 'js-cookie';

import {
  ConfigInfo,
  getConfig,
  getPaymentInfo,
  getQuotaInfo,
  getUserInfo,
  patchUserConfig,
  PatchUserConfigParams,
  APIRobotInfo,
  UserInfo,
  USER_INFO,
  cookieConfig,
} from '@mygpt/app';

import { PaymentInfo, QuotaInfo } from '../types/interface';

export interface Role {
  id: number | string;
  code: string;
  name: string;
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// 资产范围类型：null/undefined表示个人资产，组织ID表示组织资产
export type AssetScope = string | null;

const ASSET_SCOPE_KEY = 'mygpt_asset_scope';

const getStoredAssetScope = (): AssetScope => {
  try {
    const stored = localStorage.getItem(ASSET_SCOPE_KEY);
    // 清理无效值：pending_org 是历史遗留的占位值，不应作为 company_id 使用
    if (!stored || stored === 'pending_org') {
      if (stored) localStorage.removeItem(ASSET_SCOPE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};

interface PilotStoreInterface {
  userInfo?: UserInfo | undefined;
  robotInfo?: APIRobotInfo | undefined;
  paymentInfo?: PaymentInfo | undefined;
  quotaInfo?: QuotaInfo | undefined;
  configInfo?: ConfigInfo | undefined;
  roles?: Role[];
  // 当前资产范围（Cloud版有组织用户使用）：null表示个人，组织ID表示组织
  assetScope: AssetScope;
  setRoles: (roles: Role[]) => void;
  setUserInfo: (userInfo: UserInfo) => void;
  setRobotInfo: (robotInfo: APIRobotInfo) => void;
  resetRobotInfo: () => void;
  fetchUserInfo: () => Promise<UserInfo>;
  getPaymentInfo: () => Promise<PaymentInfo>;
  getQuotaInfo: () => Promise<QuotaInfo>;
  getConfigInfo: () => Promise<ConfigInfo>;
  patchUserConfig: (params: PatchUserConfigParams) => Promise<UserInfo>;
  setAssetScope: (scope: AssetScope) => void;
}

export const useGPTStore = create<PilotStoreInterface>()(
  immer<PilotStoreInterface>((set, get) => ({
    roles: [],
    // 从 localStorage 恢复，首次为 null（个人资产）
    assetScope: getStoredAssetScope(),
    setRoles: (roles: Role[]) => {
      set({ roles });
    },
    setAssetScope: (scope: AssetScope) => {
      set({ assetScope: scope });
      try {
        if (scope === null) {
          localStorage.removeItem(ASSET_SCOPE_KEY);
        } else {
          localStorage.setItem(ASSET_SCOPE_KEY, scope);
        }
      } catch {
        // localStorage 不可用时静默忽略
      }
    },
    setUserInfo: (userInfo) => {
      set({
        userInfo: userInfo,
      });
    },

    setRobotInfo: (robotInfo) => {
      set({
        robotInfo: robotInfo,
      });
    },

    resetRobotInfo: () => {
      set({
        robotInfo: undefined,
      });
    },

    fetchUserInfo: () =>
      new Promise<UserInfo>((resolve, reject) => {
        getUserInfo().then((res: { data: UserInfo }) => {
          if (!res.data) return;
          set({
            userInfo: res.data,
          });

          try {
            // 保存用户数据到 cookie 中，方便在其他子域名下使用
            Cookies.set(
              USER_INFO,
              JSON.stringify({
                name: res.data.name,
                email: res.data.email,
                user_id: res.data.user_id,
              }),
              {
                domain: cookieConfig.COOKIE_DOMAIN,
                secure: location.protocol === 'https:',
                sameSite: 'lax',
              }
            );
          } catch (error) {
            console.error(`Failed to store user info in cookies: ${error}, data: `, res.data);
          }
          resolve(res.data);
        });
      }),

    getPaymentInfo: () =>
      new Promise<PaymentInfo>((resolve, reject) => {
        getPaymentInfo().then((res: { data: PaymentInfo }) => {
          if (!res.data) return;
          set({
            paymentInfo: res.data,
          });
          resolve(res.data);
        });
      }),

    getQuotaInfo: () =>
      new Promise<QuotaInfo>((resolve, reject) => {
        getQuotaInfo().then((res: { data: QuotaInfo }) => {
          if (!res.data) return;
          set({
            quotaInfo: res.data,
          });
          resolve(res.data);
        });
      }),

    patchUserConfig: (params: PatchUserConfigParams) =>
      new Promise<UserInfo>((resolve, reject) => {
        patchUserConfig(params).then((res: { data: UserInfo }) => {
          if (!res.data) return;
          set({
            userInfo: res.data,
          });
          resolve(res.data);
        });
      }),
    getConfigInfo: () =>
      new Promise<ConfigInfo>((resolve, reject) => {
        getConfig()
          .then((res) => {
            if (!res.data) return;
            set({
              configInfo: res.data,
            });
            resolve(res.data);
          })
          .catch(reject);
      }),
  }))
);
